// src/pages/AdminLifecyclePage.jsx
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
  Bell,
  Clock,
  Moon,
  UserCheck,
  AlertTriangle,
  RefreshCw,
  Download,
} from "lucide-react";

/** Local API_BASE (same pattern as other files) */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_API_BASE?.replace(/\/+$/, "")) ||
  (process.env.REACT_APP_API_BASE &&
    process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  "http://localhost:5050";

/**
 * Expected backend shape (we are defensive if some fields are missing):
 *
 * GET /api/admin/lifecycle/summary → { ok, data }
 * data = {
 *   generatedAt: string,
 *   totalTracked: number,
 *   normalBuckets: {
 *     day10: LearnerLifecycleRow[],
 *     day20: LearnerLifecycleRow[],
 *     day30: LearnerLifecycleRow[],
 *     day40: LearnerLifecycleRow[],
 *     day50: LearnerLifecycleRow[],
 *   },
 *   countdown: LearnerLifecycleRow[],          // each has daysUntilDormant
 *   dormant: LearnerLifecycleRow[],            // dormant learners
 *   graduatesPendingDelete: LearnerLifecycleRow[]
 * }
 *
 * LearnerLifecycleRow (example):
 * {
 *   userId,
 *   fullName,
 *   email,
 *   courseSlug,
 *   courseTitle,
 *   daysSinceLastActive,
 *   daysSinceEnrolled,
 *   status,
 *   daysUntilDormant?,   // for countdown
 *   daysUntilDelete?     // for dormant/grads
 * }
 */

function StatCard({ icon: Icon, title, value, subtitle, tone = "primary" }) {
  const toneClasses =
    tone === "primary"
      ? "from-orange-600 to-amber-600"
      : tone === "warning"
      ? "from-yellow-600 to-amber-500"
      : tone === "danger"
      ? "from-red-600 to-rose-600"
      : tone === "success"
      ? "from-green-600 to-emerald-500"
      : "from-slate-600 to-slate-500";

  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-orange-100 p-5 hover:shadow-xl transition">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-gray-600 text-xs font-medium mb-1">{title}</p>
          <p
            className={`text-3xl font-bold bg-gradient-to-r ${toneClasses} bg-clip-text text-transparent`}
          >
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1 leading-snug">
              {subtitle}
            </p>
          )}
        </div>
        <div className="w-12 h-12 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl flex items-center justify-center">
          <Icon className="text-orange-600" size={24} />
        </div>
      </div>
    </div>
  );
}

function BucketTable({ rows, columns, emptyLabel }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500 text-sm border border-dashed border-orange-200 rounded-2xl bg-orange-50/40">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="overflow-auto border border-orange-100 rounded-2xl">
      <table className="w-full text-sm">
        <thead className="bg-orange-50 text-gray-700">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`p-3 text-left ${
                  col.align === "center" ? "text-center" : ""
                }`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {rows.map((row, idx) => (
            <tr
              key={row.userId + row.courseSlug + idx}
              className="border-b last:border-b-0 hover:bg-orange-50/40"
            >
              {columns.map((col) => {
                const value =
                  typeof col.render === "function"
                    ? col.render(row)
                    : row[col.key];
                return (
                  <td
                    key={col.key}
                    className={`p-3 ${
                      col.align === "center" ? "text-center" : ""
                    }`}
                  >
                    {value ?? "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminLifecyclePage() {
  const { getToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [data, setData] = useState({
    generatedAt: null,
    totalTracked: 0,
    normalBuckets: {
      day10: [],
      day20: [],
      day30: [],
      day40: [],
      day50: [],
    },
    countdown: [],
    dormant: [],
    graduatesPendingDelete: [],
  });

  const [activeTab, setActiveTab] = useState("reminders"); // reminders | countdown | dormant | graduates

  async function loadLifecycle(initial = false) {
    try {
      initial ? setLoading(true) : setRefreshing(true);
      setError("");

      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/admin/lifecycle/summary`, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || "Failed to load lifecycle summary");
      }

      const d = json.data || {};
      setData({
        generatedAt: d.generatedAt || null,
        totalTracked: d.totalTracked ?? 0,
        normalBuckets: {
          day10: d.normalBuckets?.day10 || [],
          day20: d.normalBuckets?.day20 || [],
          day30: d.normalBuckets?.day30 || [],
          day40: d.normalBuckets?.day40 || [],
          day50: d.normalBuckets?.day50 || [],
        },
        countdown: d.countdown || [],
        dormant: d.dormant || [],
        graduatesPendingDelete: d.graduatesPendingDelete || [],
      });
    } catch (e) {
      console.error("[AdminLifecyclePage] load error:", e);
      setError(e.message || "Failed to load lifecycle data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadLifecycle(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = useMemo(() => {
    const totalReminders =
      (data.normalBuckets.day10?.length || 0) +
      (data.normalBuckets.day20?.length || 0) +
      (data.normalBuckets.day30?.length || 0) +
      (data.normalBuckets.day40?.length || 0) +
      (data.normalBuckets.day50?.length || 0);

    const countdownCount = data.countdown?.length || 0;
    const dormantCount = data.dormant?.length || 0;
    const gradsCount = data.graduatesPendingDelete?.length || 0;

    return {
      totalTracked: data.totalTracked || 0,
      totalReminders,
      countdownCount,
      dormantCount,
      gradsCount,
    };
  }, [data]);

  function exportCsv() {
    // Flatten all rows with their category label
    const rows = [];

    const pushRows = (arr, category) => {
      (arr || []).forEach((row) => {
        rows.push({ ...row, category });
      });
    };

    pushRows(data.normalBuckets.day10, "Reminder: Day 10");
    pushRows(data.normalBuckets.day20, "Reminder: Day 20");
    pushRows(data.normalBuckets.day30, "Reminder: Day 30");
    pushRows(data.normalBuckets.day40, "Reminder: Day 40");
    pushRows(data.normalBuckets.day50, "Reminder: Day 50");
    pushRows(data.countdown, "Countdown to Dormant");
    pushRows(data.dormant, "Dormant");
    pushRows(data.graduatesPendingDelete, "Graduate Pending Delete");

    if (rows.length === 0) {
      alert("No lifecycle records to export yet.");
      return;
    }

    const headers = [
      "Category",
      "Full Name",
      "Email",
      "Course",
      "Days Since Enrolled",
      "Days Since Last Active",
      "Days Until Dormant",
      "Days Until Delete",
      "Status",
    ];

    const csvRows = rows.map((r) => [
      r.category,
      r.fullName ?? "",
      r.email ?? "",
      r.courseTitle ?? r.courseSlug ?? "",
      r.daysSinceEnrolled ?? "",
      r.daysSinceLastActive ?? "",
      r.daysUntilDormant ?? "",
      r.daysUntilDelete ?? "",
      r.status ?? "",
    ]);

    const csvContent = [headers, ...csvRows]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `VOP_Lifecycle_Snapshot_${new Date()
      .toISOString()
      .split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  const reminderBuckets = [
    {
      key: "day10",
      label: "Day 10 Reminder",
      description: "First gentle nudge after 10 days.",
      rows: data.normalBuckets.day10 || [],
    },
    {
      key: "day20",
      label: "Day 20 Reminder",
      description: "Second reminder for slow learners.",
      rows: data.normalBuckets.day20 || [],
    },
    {
      key: "day30",
      label: "Day 30 Reminder",
      description: "Mid-point in the 60-day window.",
      rows: data.normalBuckets.day30 || [],
    },
    {
      key: "day40",
      label: "Day 40 Reminder",
      description: "Stronger reminder (20 days left).",
      rows: data.normalBuckets.day40 || [],
    },
    {
      key: "day50",
      label: "Day 50 Reminder",
      description: "Final reminder before daily countdown.",
      rows: data.normalBuckets.day50 || [],
    },
  ];

  const baseColumns = [
    {
      key: "fullName",
      label: "Learner",
      render: (r) => (
        <div>
          <div className="font-semibold text-gray-900">
            {r.fullName || "—"}
          </div>
          <div className="text-xs text-gray-500">{r.email || "—"}</div>
        </div>
      ),
    },
    {
      key: "courseTitle",
      label: "Course",
      render: (r) => (
        <div className="text-sm text-gray-800">
          {r.courseTitle || r.courseSlug || "—"}
        </div>
      ),
    },
    {
      key: "daysSinceEnrolled",
      label: "Days Since Enrolled",
      align: "center",
      render: (r) =>
        r.daysSinceEnrolled != null ? `${r.daysSinceEnrolled} days` : "—",
    },
    {
      key: "daysSinceLastActive",
      label: "Days Since Last Active",
      align: "center",
      render: (r) =>
        r.daysSinceLastActive != null ? `${r.daysSinceLastActive} days` : "—",
    },
  ];

  const countdownColumns = [
    ...baseColumns,
    {
      key: "daysUntilDormant",
      label: "Days Until Dormant",
      align: "center",
      render: (r) =>
        r.daysUntilDormant != null ? `${r.daysUntilDormant} days` : "—",
    },
  ];

  const dormantColumns = [
    ...baseColumns,
    {
      key: "daysUntilDelete",
      label: "Days Until Delete",
      align: "center",
      render: (r) =>
        r.daysUntilDelete != null ? `${r.daysUntilDelete} days` : "—",
    },
  ];

  const gradColumns = dormantColumns;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-200 border-t-orange-600 mx-auto mb-4" />
          <p className="text-gray-700 font-semibold text-lg">
            Loading lifecycle dashboard…
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Analysing learner activity and reminder buckets
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-white pb-12">
      {/* Header */}
      <header className="bg-gradient-to-r from-orange-600 to-amber-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Bell size={26} />
              Lifecycle & Reminders
            </h1>
            <p className="text-orange-100 mt-1">
              Monitor who needs reminders, who is close to dormant, and who is
              ready for cleanup.
            </p>
            {data.generatedAt && (
              <p className="text-xs text-orange-100/80 mt-1">
                Snapshot generated:{" "}
                {new Date(data.generatedAt).toLocaleString()}
              </p>
            )}
            {error && (
              <div className="mt-2 flex items-center gap-2 bg-red-500/30 backdrop-blur-sm px-3 py-2 rounded-lg text-red-50 text-sm">
                <AlertTriangle size={16} />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => loadLifecycle(false)}
              disabled={refreshing}
              className="flex items-center gap-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 px-4 py-2 rounded-xl transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-60 text-sm"
            >
              <RefreshCw
                size={18}
                className={refreshing ? "animate-spin" : ""}
              />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <button
              onClick={exportCsv}
              className="flex items-center gap-2 bg-white text-orange-700 px-5 py-2.5 rounded-xl font-semibold hover:bg-orange-50 shadow-lg text-sm"
            >
              <Download size={18} />
              Export CSV
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Top stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={UserCheck}
            title="Total Tracked Learners"
            value={summary.totalTracked}
            subtitle="Learners currently in a lifecycle window."
            tone="primary"
          />
          <StatCard
            icon={Bell}
            title="In Reminder Buckets"
            value={summary.totalReminders}
            subtitle="Day 10, 20, 30, 40, 50 scheduled reminders."
            tone="success"
          />
          <StatCard
            icon={Clock}
            title="Countdown to Dormant"
            value={summary.countdownCount}
            subtitle="Daily reminders (day 50–60 window)."
            tone="warning"
          />
          <StatCard
            icon={Moon}
            title="Dormant + Graduates"
            value={summary.dormantCount + summary.gradsCount}
            subtitle="Dormant and pending deletion accounts."
            tone="danger"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-xl border border-orange-100 p-4 md:p-6">
          <div className="border-b border-gray-200 mb-4">
            <nav className="-mb-px flex flex-wrap gap-2">
              {[
                {
                  key: "reminders",
                  label: "Scheduled Reminders",
                  icon: Bell,
                },
                {
                  key: "countdown",
                  label: "Countdown to Dormant",
                  icon: Clock,
                },
                {
                  key: "dormant",
                  label: "Dormant Learners",
                  icon: Moon,
                },
                {
                  key: "graduates",
                  label: "Graduates Pending Delete",
                  icon: UserCheck,
                },
              ].map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 border-b-2 text-sm font-medium transition-all ${
                      isActive
                        ? "border-orange-500 text-orange-700 bg-orange-50/60 rounded-t-xl"
                        : "border-transparent text-gray-500 hover:text-orange-700 hover:border-orange-200"
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab bodies */}
          {activeTab === "reminders" && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600 mb-2">
                These learners are in the scheduled reminder windows: day 10,
                20, 30, 40, 50 (before daily countdown starts).
              </p>
              <div className="grid gap-4 lg:grid-cols-2">
                {reminderBuckets.map((bucket) => (
                  <div
                    key={bucket.key}
                    className="border border-orange-100 rounded-2xl p-4 bg-orange-50/40"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-800">
                          {bucket.label}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {bucket.description}
                        </p>
                      </div>
                      <span className="inline-flex items-center justify-center h-7 px-3 rounded-full text-xs font-semibold bg-white text-orange-700 border border-orange-100">
                        {bucket.rows.length} learner
                        {bucket.rows.length === 1 ? "" : "s"}
                      </span>
                    </div>

                    <BucketTable
                      rows={bucket.rows}
                      columns={baseColumns}
                      emptyLabel="No learners currently scheduled for this reminder."
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "countdown" && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-2">
                After day 50, learners get daily reminders until day 60. This
                section shows how many days remain before they become{" "}
                <span className="font-semibold text-orange-700">dormant</span>.
              </p>
              <BucketTable
                rows={data.countdown}
                columns={countdownColumns}
                emptyLabel="No learners currently in the daily countdown window."
              />
            </div>
          )}

          {activeTab === "dormant" && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-2">
                Learners who passed the inactivity limit and are now marked as{" "}
                <span className="font-semibold text-orange-700">dormant</span>.
                Accounts here will be candidates for deletion after their
                dormancy window ends.
              </p>
              <BucketTable
                rows={data.dormant}
                columns={dormantColumns}
                emptyLabel="No dormant learners yet. 🎉"
              />
            </div>
          )}

          {activeTab === "graduates" && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-2">
                Graduates that have had more than{" "}
                <span className="font-semibold text-orange-700">
                  30 days of inactivity
                </span>{" "}
                and are queued for account cleanup.
              </p>
              <BucketTable
                rows={data.graduatesPendingDelete}
                columns={gradColumns}
                emptyLabel="No graduates pending deletion yet."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
